import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import {
	CancellationReasonPassengerBookingEnum,
	PassengerBookingView,
} from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';
import {
	checkIsLongDelayFreeCancellation,
	findCancellationFee,
	formatPrice,
	getItineraryTimingV2,
	isPRODTItinerary,
	isSpecialSchoolTrafficItinerary,
} from '@mopla/utils';

import { MplButton } from '../../../atoms/MplButton/MplButton';
import { CompleteAnim } from '../../../molecules/Animations';
import { MplErrorModal } from '../../MplModal/MplErrorModal';
import { MplModal } from '../../MplModal/MplModal';
import { usePassengerRoutingHelpers } from '../../WithPassengerRoutingHelpers/WithPassengerRoutingHelpers';

import { FreeCancellationCancelModal } from './FreeCancellationCancelModal';
import { ReasonFormStep } from './ReasonFormStep';
import { SchoolConfirmationModal } from './SchoolConfirmationModal';
import { ICancellationReasonFormState, TCancellationStep } from './types';
import { useCancelBookingRequest } from './useCancelBookingRequest';
import { useCancellationReasonForm } from './useCancellationReasonForm';

interface ICancellationFlowProps {
	bookedItinerary: PassengerBookingView;
	onClose: VoidFunction;
}

export const CancellationFlow: FC<ICancellationFlowProps> = ({
	bookedItinerary,
	onClose,
}) => {
	const [step, setStep] = useState<TCancellationStep>(null);
	const { cancelBooking, isCancelling } = useCancelBookingRequest();
	const { navigateToTickets } = usePassengerRoutingHelpers();
	const { t } = useTranslation('tickets');
	const [cancellationCost, setCancellationCost] = useState<0 | string | null>(
		null
	);

	const isPRODT = isPRODTItinerary(bookedItinerary.itinerary);
	const isSchool = isSpecialSchoolTrafficItinerary(bookedItinerary.itinerary);
	const itineraryTimings = getItineraryTimingV2(bookedItinerary.itinerary.legs);

	const cancellationReasonForm = useCancellationReasonForm();
	const { handleSubmit: cancellationReasonFormSubmit, setValue } =
		cancellationReasonForm;

	const getCancellationCost = useCallback(
		(bookedTicket: PassengerBookingView) => {
			const price = findCancellationFee(bookedTicket.itinerary);

			return price === 0 ? 0 : formatPrice(price / 100);
		},
		[]
	);

	useEffect(
		() => {
			/** Init the Cancellation flow */
			const cancellationCost = getCancellationCost(bookedItinerary);
			const isLongDelayFreeCancellation = checkIsLongDelayFreeCancellation(
				bookedItinerary.itinerary
			);

			setCancellationCost(cancellationCost);

			/** School (RMBLS) rides skip the reason form — a single confirm modal.
			 *  PRODT rides with a long-delay free cancellation also confirm-only.
			 *  Otherwise, follow the regular reason-form flow. */
			if (isSchool || isLongDelayFreeCancellation) {
				setStep('confirmation');
			} else {
				setStep('reason');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const processCancelBooking = useCallback(
		async (formState: ICancellationReasonFormState) => {
			const isSuccess = await cancelBooking({
				bookingNumber: bookedItinerary.bookingNumber,
				cancellationReason: formState.reason,
				cancellationReasonDescription: formState.otherReasonText,
			});
			setStep(isSuccess ? 'success' : 'error');
		},
		[cancelBooking, bookedItinerary]
	);

	const formSubmitHandler = useMemo(() => {
		return cancellationReasonFormSubmit(processCancelBooking);
	}, [cancellationReasonFormSubmit, processCancelBooking]);

	const finishHandler = useCallback(() => {
		navigateToTickets(undefined, { replace: true });
		onClose();
	}, [navigateToTickets, onClose]);

	const longDelayFreeCancelHandler = useCallback(() => {
		setValue('reason', CancellationReasonPassengerBookingEnum.DELAY);
		formSubmitHandler();
	}, [formSubmitHandler, setValue]);

	/** School rides skip the reason form entirely, so we submit directly with
	 *  OTHER_REASON instead of going through the form (which would require a
	 *  description for OTHER_REASON). */
	const schoolCancelHandler = useCallback(async () => {
		const isSuccess = await cancelBooking({
			bookingNumber: bookedItinerary.bookingNumber,
			cancellationReason: CancellationReasonPassengerBookingEnum.OTHER_REASON,
		});
		setStep(isSuccess ? 'success' : 'error');
	}, [cancelBooking, bookedItinerary]);

	return (
		<FormProvider {...cancellationReasonForm}>
			<MplErrorModal
				open={step === 'error'}
				testId="CancellationFlow-MplErrorModal"
				buttons={
					<MplButton fullWidth onClick={onClose}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
			<FreeCancellationCancelModal
				open={step === 'confirmation' && !isSchool}
				onClose={onClose}
				onConfirm={longDelayFreeCancelHandler}
				isCanceling={isCancelling}
			/>
			<SchoolConfirmationModal
				open={step === 'confirmation' && isSchool}
				onClose={onClose}
				onConfirm={schoolCancelHandler}
				isCanceling={isCancelling}
			/>
			<ReasonFormStep
				isOpen={step === 'reason'}
				onCancel={onClose}
				onSubmit={formSubmitHandler}
				cancellationPrice={cancellationCost}
				isPRODT={isPRODT}
				withDelayReason={itineraryTimings.isDelayed}
			/>
			<MplModal
				open={step === 'success'}
				icon={<CompleteAnim />}
				testId="CancellationFlow-MplModal"
				title={t('cancellation_flow.success.title')}
				buttons={
					<MplButton fullWidth onClick={finishHandler}>
						{t('common:button.ok')}
					</MplButton>
				}
			/>
		</FormProvider>
	);
};
